import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowSquareOut, DownloadSimple, FileArrowDown, House, ImageSquare, Lightning, Plus, Trash, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, adminHeaders, getSecret, fileUrl } from "@/lib/api";
import { Lightbox } from "@/components/Lightbox";
import { RefreshButton } from "@/components/RefreshButton";

function StatusChip({ label, done, doneText, undoneText, busy, onToggle, testid }) {
    return (
        <button
            data-testid={testid}
            onClick={() => onToggle(!done)}
            disabled={busy}
            title={done ? "Klik untuk tandai belum" : "Klik untuk tandai sudah"}
            className={`text-xs px-2.5 py-1.5 border flex items-center gap-1.5 transition-colors disabled:opacity-50 ${
                done
                    ? "border-green-600 text-green-700 bg-green-50 hover:bg-green-100"
                    : "border-amber-500 text-amber-700 bg-amber-50 hover:bg-amber-100"
            }`}
        >
            <span className={`w-3.5 h-3.5 border flex items-center justify-center text-[10px] ${done ? "bg-green-600 border-green-600 text-white" : "border-amber-500"}`}>
                {done && "✓"}
            </span>
            {label}: {done ? doneText : undoneText}
        </button>
    );
}

const GROUPS = [
    { key: "finishing_budhi", label: "Finishing di Budhi", testid: "finishing-group-budhi" },
    { key: "finishing_cibogo", label: "Finishing di Cibogo", testid: "finishing-group-cibogo" },
];

export default function FinishingPage() {
    const navigate = useNavigate();
    const [rooms, setRooms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusBusy, setStatusBusy] = useState("");
    const [lightbox, setLightbox] = useState(null);
    const [expandedRenders, setExpandedRenders] = useState(() => new Set());
    const toggleRenders = (id) =>
        setExpandedRenders((s) => {
            const n = new Set(s);
            if (n.has(id)) n.delete(id);
            else n.add(id);
            return n;
        });
    const [showAdd, setShowAdd] = useState("");
    const [properties, setProperties] = useState([]);
    const [addPropId, setAddPropId] = useState("");
    const [propRooms, setPropRooms] = useState([]);
    const [addBusy, setAddBusy] = useState("");

    const load = useCallback(async () => {
        const res = await api.get("/admin/finishing", { headers: adminHeaders() });
        setRooms(res.data);
    }, []);

    const toggleStatus = async (roomId, field, value) => {
        setStatusBusy(roomId + field);
        try {
            await api.patch(`/admin/rooms/${roomId}/status`, { field, value }, { headers: adminHeaders() });
            await load();
        } catch {
            toast.error("Gagal mengubah status");
        } finally {
            setStatusBusy("");
        }
    };

    const deletePola = async (roomId, path) => {
        try {
            await api.delete(`/admin/rooms/${roomId}/pola-warna`, { data: { path }, headers: adminHeaders() });
            toast.success("Pola warna dihapus");
            await load();
        } catch {
            toast.error("Gagal menghapus pola warna");
        }
    };

    const downloadPolaZip = async (room) => {
        setStatusBusy(room.id + "zip");
        try {
            const res = await api.get(`/admin/rooms/${room.id}/pola-warna.zip`, { headers: adminHeaders(), responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `pola-warna-${room.name.replace(/\s+/g, "-")}.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Gagal mengunduh pola warna");
        } finally {
            setStatusBusy("");
        }
    };

    const finishRoom = async (roomId) => {
        setStatusBusy(roomId + "selesai");
        try {
            await api.patch(`/admin/rooms/${roomId}/production-status`, { value: "siap_kirim" }, { headers: adminHeaders() });
            toast.success("Selesai finishing — status jadi Siap Kirim");
            await load();
        } catch {
            toast.error("Gagal mengubah status");
        } finally {
            setStatusBusy("");
        }
    };

    const openAdd = async (groupKey) => {
        setShowAdd(groupKey);
        setAddPropId("");
        setPropRooms([]);
        try {
            const res = await api.get("/admin/properties", { headers: adminHeaders() });
            setProperties(res.data);
        } catch {
            toast.error("Gagal memuat properti");
        }
    };

    const pickProperty = async (propId) => {
        setAddPropId(propId);
        if (!propId) {
            setPropRooms([]);
            return;
        }
        try {
            const res = await api.get(`/admin/properties/${propId}/rooms`, { headers: adminHeaders() });
            setPropRooms(res.data);
        } catch {
            toast.error("Gagal memuat ruangan");
        }
    };

    const addToGroup = async (roomId, groupKey) => {
        setAddBusy(roomId);
        try {
            await api.patch(`/admin/rooms/${roomId}/production-status`, { value: groupKey }, { headers: adminHeaders() });
            toast.success(groupKey === "finishing_budhi" ? "Masuk Finishing Budhi" : "Masuk Finishing Cibogo");
            await load();
            if (addPropId) await pickProperty(addPropId);
        } catch {
            toast.error("Gagal menambahkan");
        } finally {
            setAddBusy("");
        }
    };

    useEffect(() => {
        if (!getSecret()) {
            navigate("/admin");
            return;
        }
        load()
            .catch(() => toast.error("Gagal memuat daftar finishing"))
            .finally(() => setLoading(false));
    }, [load, navigate]);

    return (
        <div className="min-h-screen flex flex-col" data-testid="finishing-page">
            <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
                <div className="max-w-6xl mx-auto w-full px-6 py-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <button
                            data-testid="finishing-back"
                            onClick={() => navigate("/admin")}
                            className="text-muted-foreground hover:text-primary transition-colors"
                            title="Kembali"
                        >
                            <ArrowLeft size={20} weight="light" />
                        </button>
                        <img src="/logo.png" alt="INERRE interior" className="h-10 object-contain" />
                    </div>
                    <div className="text-center">
                        <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-muted-foreground">Sedang di-Finishing</p>
                        <h1 className="font-serif text-2xl sm:text-3xl tracking-tight font-medium">Semua Finishing</h1>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                        <RefreshButton />
                        <button
                            data-testid="finishing-home-btn"
                            onClick={() => navigate("/admin")}
                            className="text-muted-foreground hover:text-primary transition-colors shrink-0"
                            title="Kembali ke pemindai"
                        >
                            <House size={20} weight="light" />
                        </button>
                    </div>
                </div>
            </header>

            <main className="flex-1 w-full max-w-6xl mx-auto px-6 py-6">
                {loading ? (
                    <p className="text-sm text-muted-foreground" data-testid="finishing-loading">Memuat...</p>
                ) : rooms.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-16" data-testid="finishing-empty">
                        Tidak ada ruangan dengan status "Sedang di-Finishing".
                    </p>
                ) : (
                    GROUPS.map((group) => {
                        const groupRooms = rooms.filter((r) => r.production_status === group.key);
                        return (
                            <section key={group.key} className="mb-10" data-testid={group.testid}>
                                <div className="flex items-center gap-3 mb-4 flex-wrap">
                                    <h2 className="font-serif text-xl sm:text-2xl tracking-tight font-medium">{group.label}</h2>
                                    <span className="text-xs text-muted-foreground" data-testid={`${group.testid}-count`}>
                                        {groupRooms.length} ruangan
                                    </span>
                                    <button
                                        data-testid={`${group.testid}-add-btn`}
                                        onClick={() => openAdd(group.key)}
                                        className="ml-auto bg-primary text-primary-foreground px-3 py-2 text-xs font-medium hover:bg-[#9c4a2a] transition-colors flex items-center gap-1.5"
                                    >
                                        <Plus size={14} weight="bold" /> Tambah
                                    </button>
                                </div>

                                {showAdd === group.key && (
                                    <div className="border border-border bg-white p-4 mb-4 space-y-3" data-testid={`${group.testid}-add-panel`}>
                                        <div className="flex items-center justify-between">
                                            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">
                                                Tambah Ruangan ke {group.label}
                                            </p>
                                            <button data-testid={`${group.testid}-add-close`} onClick={() => setShowAdd("")} className="text-muted-foreground hover:text-primary">
                                                <X size={18} />
                                            </button>
                                        </div>
                                        <select
                                            data-testid={`${group.testid}-add-prop`}
                                            value={addPropId}
                                            onChange={(e) => pickProperty(e.target.value)}
                                            className="w-full border border-border px-3 py-2 text-sm outline-none focus:border-primary bg-white"
                                        >
                                            <option value="">— Pilih proyek —</option>
                                            {properties.map((p) => (
                                                <option key={p.id} value={p.id}>
                                                    {p.name}
                                                </option>
                                            ))}
                                        </select>
                                        {addPropId && (
                                            <div className="space-y-2" data-testid={`${group.testid}-add-rooms`}>
                                                {propRooms.length === 0 && <p className="text-xs text-muted-foreground">Proyek ini belum punya ruangan.</p>}
                                                {propRooms.map((r) => {
                                                    const already = r.production_status === group.key;
                                                    return (
                                                        <div key={r.id} className="flex items-center justify-between border border-border px-3 py-2" data-testid={`${group.testid}-add-room-${r.id}`}>
                                                            <span className="text-sm">
                                                                {r.name}
                                                                {already && <span className="ml-2 text-[10px] uppercase tracking-widest text-muted-foreground">(sudah di grup ini)</span>}
                                                            </span>
                                                            <button
                                                                data-testid={`${group.testid}-add-room-btn-${r.id}`}
                                                                onClick={() => addToGroup(r.id, group.key)}
                                                                disabled={already || addBusy === r.id}
                                                                className="text-xs border border-primary text-primary px-3 py-1.5 hover:bg-primary hover:text-primary-foreground transition-colors disabled:opacity-40"
                                                            >
                                                                {already ? "Sudah masuk" : "+ Tambah"}
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {groupRooms.length === 0 ? (
                                    <p className="text-sm text-muted-foreground border border-dashed border-border px-4 py-6 text-center">
                                        Belum ada ruangan di grup ini.
                                    </p>
                                ) : (
                                    <div className="space-y-6">
                                        {groupRooms.map((room) => {
                                            const hasWarning = !room.rendering_workshop || !room.order_finishing || (room.pola_warna?.length || 0) === 0;
                                            return (
                                            <div
                                                key={room.id}
                                                className={`border ${hasWarning ? "border-red-300 bg-red-50/40" : "border-border bg-white"}`}
                                                data-testid={`finishing-room-${room.id}`}
                                            >
                                                <div className="p-4 border-b border-border flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                                    <div>
                                                        <h3 className="font-serif text-xl sm:text-2xl tracking-tight font-medium" data-testid={`finishing-name-${room.id}`}>
                                                            {room.name}
                                                        </h3>
                                                        <p className="text-xs text-muted-foreground mt-1" data-testid={`finishing-prop-${room.id}`}>
                                                            {room.property_name}
                                                        </p>
                                                    </div>
                                                    <div className="flex flex-wrap gap-2 sm:justify-end sm:shrink-0">
                                                        <button
                                                            data-testid={`finishing-done-${room.id}`}
                                                            onClick={() => finishRoom(room.id)}
                                                            disabled={statusBusy === room.id + "selesai"}
                                                            className="flex-1 sm:flex-none text-center border border-green-700 text-green-700 px-3 py-2 text-xs hover:bg-green-700 hover:text-white transition-colors disabled:opacity-50"
                                                        >
                                                            Selesai Finishing
                                                        </button>
                                                        <a
                                                            data-testid={`finishing-open-${room.id}`}
                                                            href={`/a/${room.id}`}
                                                            className="flex-1 sm:flex-none justify-center border border-border px-3 py-2 text-xs hover:border-primary hover:text-primary transition-colors flex items-center gap-1.5"
                                                        >
                                                            <ArrowSquareOut size={14} weight="light" /> Buka Ruangan
                                                        </a>
                                                    </div>
                                                </div>
                                                {hasWarning && (
                                                    <p
                                                        className="px-4 py-2 bg-red-100/70 border-b border-red-200 text-[11px] font-bold uppercase tracking-wider text-red-700"
                                                        data-testid={`finishing-warning-${room.id}`}
                                                    >
                                                        Perlu perhatian: {!room.rendering_workshop && "rendering belum di workshop"}
                                                        {!room.rendering_workshop && (!room.order_finishing || (room.pola_warna?.length || 0) === 0) && " • "}
                                                        {!room.order_finishing && "bahan finishing belum diorder"}
                                                        {!room.order_finishing && (room.pola_warna?.length || 0) === 0 && " • "}
                                                        {(room.pola_warna?.length || 0) === 0 && "pola warna belum di-upload"}
                                                    </p>
                                                )}

                                                <div className="p-4 space-y-4">
                                                    <div>
                                                        <div className="flex items-center justify-between gap-2 mb-2">
                                                            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Pola Warna</p>
                                                            {room.pola_warna?.length > 0 && (
                                                                <button
                                                                    data-testid={`finishing-pola-zip-${room.id}`}
                                                                    onClick={() => downloadPolaZip(room)}
                                                                    disabled={statusBusy === room.id + "zip"}
                                                                    className="border border-border px-2.5 py-1.5 text-xs hover:border-primary hover:text-primary transition-colors flex items-center gap-1.5 disabled:opacity-50"
                                                                >
                                                                    <DownloadSimple size={14} weight="light" />
                                                                    {statusBusy === room.id + "zip" ? "Menyiapkan..." : "Download ZIP"}
                                                                </button>
                                                            )}
                                                        </div>
                                                        {room.pola_warna?.length > 0 ? (
                                                            <>
                                                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid={`finishing-pola-${room.id}`}>
                                                                {(expandedRenders.has(room.id + ":pola") || room.pola_warna.length <= 4 ? room.pola_warna : room.pola_warna.slice(0, 3)).map((path, i) => (
                                                                    <div
                                                                        key={path}
                                                                        className="relative group aspect-[4/3] bg-secondary overflow-hidden border border-border cursor-zoom-in"
                                                                        onClick={() => setLightbox({ images: room.pola_warna, index: i })}
                                                                        data-testid={`finishing-pola-${room.id}-${i}`}
                                                                    >
                                                                        <img
                                                                            src={fileUrl(path)}
                                                                            alt={`Pola warna ${room.name} ${i + 1}`}
                                                                            className="w-full h-full object-cover"
                                                                        />
                                                                        <button
                                                                            data-testid={`finishing-pola-delete-${room.id}-${i}`}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                deletePola(room.id, path);
                                                                            }}
                                                                            className="absolute top-1 right-1 z-10 bg-white/90 border border-border p-1 text-muted-foreground hover:text-destructive transition-colors"
                                                                            title="Hapus pola warna"
                                                                        >
                                                                            <Trash size={12} weight="bold" />
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                                {!expandedRenders.has(room.id + ":pola") && room.pola_warna.length > 4 && (
                                                                    <button
                                                                        onClick={() => toggleRenders(room.id + ":pola")}
                                                                        data-testid={`finishing-pola-more-${room.id}`}
                                                                        className="aspect-[4/3] border border-dashed border-border text-xs text-muted-foreground hover:border-primary hover:text-primary transition-colors flex items-center justify-center"
                                                                    >
                                                                        +{room.pola_warna.length - 3} lainnya
                                                                    </button>
                                                                )}
                                                            </div>
                                                            {expandedRenders.has(room.id + ":pola") && room.pola_warna.length > 4 && (
                                                                <button
                                                                    onClick={() => toggleRenders(room.id + ":pola")}
                                                                    data-testid={`finishing-pola-less-${room.id}`}
                                                                    className="text-xs text-primary hover:underline mt-2"
                                                                >
                                                                    Sembunyikan
                                                                </button>
                                                            )}
                                                            </>
                                                        ) : (
                                                            <div className="aspect-[4/3] max-w-xs bg-secondary flex items-center justify-center border border-border" data-testid={`finishing-no-pola-${room.id}`}>
                                                                <ImageSquare size={28} weight="light" className="text-muted-foreground" />
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div>
                                                        <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-2">Rendering</p>
                                                        {room.renderings?.length > 0 ? (
                                                            <>
                                                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid={`finishing-renderings-${room.id}`}>
                                                                {(expandedRenders.has(room.id) || room.renderings.length <= 4 ? room.renderings : room.renderings.slice(0, 3)).map((path, i) => (
                                                                    <button
                                                                        key={path}
                                                                        onClick={() => setLightbox({ images: room.renderings, index: i })}
                                                                        className="aspect-[4/3] bg-secondary overflow-hidden border border-border block cursor-zoom-in"
                                                                        data-testid={`finishing-rendering-${room.id}-${i}`}
                                                                    >
                                                                        <img
                                                                            src={fileUrl(path)}
                                                                            alt={`Rendering ${room.name} ${i + 1}`}
                                                                            className="w-full h-full object-cover"
                                                                        />
                                                                    </button>
                                                                ))}
                                                                {!expandedRenders.has(room.id) && room.renderings.length > 4 && (
                                                                    <button
                                                                        onClick={() => toggleRenders(room.id)}
                                                                        data-testid={`finishing-more-renderings-${room.id}`}
                                                                        className="aspect-[4/3] border border-dashed border-border text-xs text-muted-foreground hover:border-primary hover:text-primary transition-colors flex items-center justify-center"
                                                                    >
                                                                        +{room.renderings.length - 3} lainnya
                                                                    </button>
                                                                )}
                                                            </div>
                                                            {expandedRenders.has(room.id) && room.renderings.length > 4 && (
                                                                <button
                                                                    onClick={() => toggleRenders(room.id)}
                                                                    data-testid={`finishing-less-renderings-${room.id}`}
                                                                    className="text-xs text-primary hover:underline mt-2"
                                                                >
                                                                    Sembunyikan
                                                                </button>
                                                            )}
                                                            </>
                                                        ) : (
                                                            <div className="aspect-[4/3] max-w-xs bg-secondary flex items-center justify-center border border-border" data-testid={`finishing-no-rendering-${room.id}`}>
                                                                <ImageSquare size={28} weight="light" className="text-muted-foreground" />
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="flex flex-wrap gap-2">
                                                        {room.me_link && (
                                                            <a
                                                                data-testid={`finishing-me-${room.id}`}
                                                                href={room.me_link}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="border border-border px-3 py-2 text-xs hover:border-primary hover:text-primary transition-colors flex items-center gap-1.5"
                                                            >
                                                                <Lightning size={14} weight="light" /> Gambar ME / Electrical
                                                            </a>
                                                        )}
                                                        {room.gambar_kerja_link && (
                                                            <a
                                                                data-testid={`finishing-gk-${room.id}`}
                                                                href={room.gambar_kerja_link}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="border border-border px-3 py-2 text-xs hover:border-primary hover:text-primary transition-colors flex items-center gap-1.5"
                                                            >
                                                                <FileArrowDown size={14} weight="light" /> Gambar Kerja
                                                                <span
                                                                    className="ml-1 text-[10px] font-bold tracking-widest uppercase border border-primary text-primary px-1.5 py-0.5"
                                                                    data-testid={`finishing-gk-version-${room.id}`}
                                                                >
                                                                    {room.gambar_kerja_version || "Tanpa versi"}
                                                                </span>
                                                            </a>
                                                        )}
                                                        {!room.me_link && !room.gambar_kerja_link && (
                                                            <p className="text-xs text-muted-foreground" data-testid={`finishing-no-links-${room.id}`}>
                                                                Belum ada link Google Drive.
                                                            </p>
                                                        )}
                                                    </div>

                                                    <div className="flex flex-wrap gap-2" data-testid={`finishing-statuses-${room.id}`}>
                                                        <StatusChip
                                                            label="Rendering di Workshop"
                                                            done={room.rendering_workshop}
                                                            doneText="Sudah"
                                                            undoneText="Belum"
                                                            busy={statusBusy === room.id + "rendering_workshop"}
                                                            onToggle={(v) => toggleStatus(room.id, "rendering_workshop", v)}
                                                            testid={`finishing-rws-${room.id}`}
                                                        />
                                                        <StatusChip
                                                            label="Bahan Finishing"
                                                            done={room.order_finishing}
                                                            doneText="Sudah diorder"
                                                            undoneText="Belum diorder"
                                                            busy={statusBusy === room.id + "order_finishing"}
                                                            onToggle={(v) => toggleStatus(room.id, "order_finishing", v)}
                                                            testid={`finishing-order-${room.id}`}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </section>
                        );
                    })
                )}
            </main>

            {lightbox && (
                <Lightbox
                    src={fileUrl(lightbox.images[lightbox.index])}
                    alt="Rendering"
                    onClose={() => setLightbox(null)}
                    onPrev={lightbox.images.length > 1 ? () => setLightbox((s) => ({ ...s, index: (s.index - 1 + s.images.length) % s.images.length })) : null}
                    onNext={lightbox.images.length > 1 ? () => setLightbox((s) => ({ ...s, index: (s.index + 1) % s.images.length })) : null}
                />
            )}
        </div>
    );
}
